import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ReportPageLayout from "@/components/reports/layout/ReportPageLayout";
import ReportFilterPanel, {
    ReportDateInput,
    ReportNumberInput,
    ReportSelectInput,
} from "@/components/reports/layout/ReportFilterPanel";
import ExportSalespersonReportView from "@/components/reports/export/ExportSalespersonReportView";
import {
    EXPORT_SALESPERSON_BOOKING_COLUMNS,
    EXPORT_SALESPERSON_CONTACT_COLUMNS,
    EXPORT_SALESPERSON_CONTAINER_TEUS_COLUMNS,
    EXPORT_SALESPERSON_TEUS_COLUMNS,
} from "@/components/reports/export/exportSalespersonReportColumns";
import { getFclExportBookingSalespersonReport } from "@/redux/FCLExportReports/actions";
import { buildFclExportSalespersonParams } from "@/utility/fclReportHelpers";
import { defaultExportSalespersonFilters } from "@/pages/reports/shared/reportDefaults";
import { useFclReportLoader, useMountReportLoad } from "@/pages/reports/shared/useFclReportLoader";

const REPORT_BY_OPTIONS = [
    { value: "ETS", label: "ETS" },
    { value: "ETA", label: "ETA" },
];

export default function FclExportBookingSalespersonReportPage() {
    const {
        exportSalespersonReportList,
        exportSalespersonReportSummary,
        exportSalespersonReportContainerTeus,
        exportSalespersonReportSalesPersonTeus,
        exportSalespersonReportContactList,
    } = useSelector((state) => state.fclExportReports);

    const [filters, setFilters] = useState(defaultExportSalespersonFilters);
    const { loadReport } = useFclReportLoader(
        getFclExportBookingSalespersonReport,
        buildFclExportSalespersonParams
    );

    useMountReportLoad(loadReport, filters);

    const resetFilters = () => {
        const next = defaultExportSalespersonFilters();
        setFilters(next);
        loadReport(next);
    };

    const exportSheets = useMemo(() => {
        const sheets = [
            {
                name: "Booking List",
                data: exportSalespersonReportList || [],
                columns: EXPORT_SALESPERSON_BOOKING_COLUMNS,
            },
            {
                name: "Sales Person TEUS",
                data: exportSalespersonReportSalesPersonTeus || [],
                columns: EXPORT_SALESPERSON_TEUS_COLUMNS,
            },
            {
                name: "Contact List",
                data: exportSalespersonReportContactList || [],
                columns: EXPORT_SALESPERSON_CONTACT_COLUMNS,
            },
            {
                name: "Container TEUS",
                data: exportSalespersonReportContainerTeus || [],
                columns: EXPORT_SALESPERSON_CONTAINER_TEUS_COLUMNS,
            },
        ];
        if (
            exportSalespersonReportSummary &&
            Object.keys(exportSalespersonReportSummary).length > 0
        ) {
            sheets.push({ name: "Summary", data: [exportSalespersonReportSummary] });
        }
        return sheets;
    }, [
        exportSalespersonReportList,
        exportSalespersonReportSummary,
        exportSalespersonReportContainerTeus,
        exportSalespersonReportSalesPersonTeus,
        exportSalespersonReportContactList,
    ]);

    return (
        <ReportPageLayout
            title="Booking Salesperson Report"
            breadcrumb="OCEAN FREIGHT • EXPORT • REPORTS"
            hubPath="/ocean/export/reports"
            onRefresh={() => loadReport(filters)}
            exportSheets={exportSheets}
            exportFileName="Export_Booking_Salesperson_Report"
            filterPanel={
                <ReportFilterPanel onSubmit={() => loadReport(filters)} onReset={resetFilters}>
                    <ReportDateInput
                        label="From date"
                        name="FromDate"
                        value={filters.FromDate}
                        onChange={(field, val) => setFilters((p) => ({ ...p, [field]: val ?? "" }))}
                    />
                    <ReportDateInput
                        label="To date"
                        name="ToDate"
                        value={filters.ToDate}
                        onChange={(field, val) => setFilters((p) => ({ ...p, [field]: val ?? "" }))}
                    />
                    <ReportSelectInput
                        label="Report by"
                        value={filters.ReportBy}
                        onChange={(e) => setFilters((p) => ({ ...p, ReportBy: e.target.value }))}
                        options={REPORT_BY_OPTIONS}
                    />
                    <ReportNumberInput
                        label="Sales person ID"
                        value={filters.SalesPersonId ?? ""}
                        onChange={(e) =>
                            setFilters((p) => ({ ...p, SalesPersonId: e.target.value }))
                        }
                        placeholder="Optional"
                    />
                </ReportFilterPanel>
            }
        >
            <ExportSalespersonReportView
                filters={filters}
                summary={exportSalespersonReportSummary}
                bookingList={exportSalespersonReportList}
                salesPersonTeus={exportSalespersonReportSalesPersonTeus}
                contactList={exportSalespersonReportContactList}
                containerTeusList={exportSalespersonReportContainerTeus}
            />
        </ReportPageLayout>
    );
}
